import classNames from 'classnames/bind'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ButtonText,
  DateInput,
  Dropzone,
  DropzoneRejectionCode,
  Feedback,
  Flex,
  FormField,
  MarkdownTextArea,
  MultiSelect,
  Select,
  TextInput
} from '@percent/lemonade'
import { DOCUMENT_UPLOAD_ACCEPT, MAX_GRANT_DOCUMENT_FILES } from '@percent/domain/grants'
import { CandidateTaskFormQuestionProps } from './CandidateTaskFormQuestion.types'
import { CandidateFormDecisionKey } from '@percent/cause-dashboard/services/grants/grantsService.types'
import styles from './CandidateTaskFormQuestion.module.scss'
import { CountrySelect } from '@percent/cause-dashboard/app/grants/components/countrySelect/CountrySelect'
import { Alpha3Code } from 'i18n-iso-countries'
import { MAX_TEXTAREA_INPUT_LEN } from '../CandidateTaskForm'

const cx = classNames.bind(styles)

export function CandidateTaskFormQuestion({
  question,
  handleChange,
  handleBlur,
  error,
  touched,
  value,
  disabled = false
}: Readonly<CandidateTaskFormQuestionProps>) {
  const { id, type, placeholder, required, label, description, instructions } = question
  const { t } = useTranslation()
  const options = useMemo(() => ('options' in question ? question.options : []), [question])
  const [textAreaCharCount, setTextAreaCharCount] = useState(typeof value === 'string' ? value.length : 0)
  const renderDescription = () => {
    // We don't want to show a description for documents as it's handled by the dropzone component
    if (type === 'documents') {
      return undefined
    }

    return description
  }

  const renderQuestion = () => {
    switch (type) {
      case 'score': {
        const { scoreWeight } = question
        const hasWeight = typeof scoreWeight === 'number'

        return (
          <Flex gap={8} justify="space-between" flex={1} data-testid={`${id}-score-buttons`}>
            {Array.from({ length: question.maxScore }, (_, i) => {
              const score = i + 1
              const submittedValue = hasWeight ? scoreWeight * score : score

              return (
                <ButtonText
                  key={`${id}-${score}`}
                  className={cx({
                    scoreButton: true,
                    active: value === submittedValue
                  })}
                  onClick={() => handleChange(id, submittedValue)}
                  data-testid={`q-${id}-btn-score-${score}`}
                  disabled={disabled}
                >
                  {submittedValue}
                </ButtonText>
              )
            })}
          </Flex>
        )
      }
      case 'textarea': {
        const maxLength = ('maxLength' in question && question.maxLength) || MAX_TEXTAREA_INPUT_LEN
        const charsRemaining = maxLength - textAreaCharCount

        return (
          <Flex direction="column" w="100%">
            <MarkdownTextArea
              name={id}
              placeholder={placeholder}
              content={value as string}
              onChange={value => {
                setTextAreaCharCount(value.length)
                handleChange(id, value)
              }}
              testId={`q-${id}-text-area`}
              status={error && touched ? 'danger' : undefined}
              maxLength={maxLength}
              disabled={disabled}
            />
            <Feedback variant={charsRemaining === 0 || charsRemaining < 0 ? 'critical' : 'default'} align="start">
              {t('grants.grantDetails.candidateTasks.form.charactersRemaining', { count: charsRemaining })}
            </Feedback>
          </Flex>
        )
      }
      case 'select':
        if (question.multiple) {
          return (
            <MultiSelect
              name={id}
              placeholder={placeholder}
              defaultValue={options.filter(
                option => Array.isArray(value) && (value as string[]).includes(option.value)
              )}
              onChange={selected =>
                handleChange(
                  id,
                  selected.map(s => s.value)
                )
              }
              options={options}
              searchable
              data-testid={`q-${id}-multiselect`}
              disabled={disabled}
            />
          )
        }

        return (
          <Select
            name={id}
            placeholder={placeholder}
            defaultValue={options.find(option => option.value === value)}
            onChange={e => handleChange(id, e.value)}
            options={options}
            searchable
            data-testid={`q-${id}-select`}
            disabled={disabled}
          />
        )

      case 'country_search':
        return (
          <CountrySelect
            name={id}
            value={value as Alpha3Code}
            placeholder={placeholder}
            onChange={newValue => {
              handleChange(id, newValue.value)
            }}
            data-testid={`q-${id}-country-select`}
            disabled={disabled}
          />
        )
      case 'decision':
        return (
          <Flex gap={8} flex={1}>
            {(['fail', 'pass'] as CandidateFormDecisionKey[]).map(key => {
              const option = question.options.find(o => o.key === key)

              return (
                <ButtonText
                  key={key}
                  onClick={() => handleChange(id, option?.value ?? '')}
                  className={cx({
                    decisionButton: true,
                    [key]: true,
                    active: value === option?.value
                  })}
                  data-testid={`q-${id}-decision-${key}`}
                  disabled={disabled}
                >
                  {option?.label}
                </ButtonText>
              )
            })}
          </Flex>
        )
      case 'documents':
        return (
          <Flex direction="column" w="100%">
            <Dropzone
              title={t('grants.grantDetails.candidateTasks.form.dropzone.title')}
              description={t('grants.grantDetails.candidateTasks.form.dropzone.description', {
                maxFiles: MAX_GRANT_DOCUMENT_FILES
              })}
              files={(value as File[]) || []}
              onChange={files => handleChange(id, files)}
              accept={DOCUMENT_UPLOAD_ACCEPT}
              maxFiles={MAX_GRANT_DOCUMENT_FILES}
              testId={`q-${id}-dropzone`}
              disabled={disabled}
              getErrorMessage={({ fileName, code, maxSizeFormatted, acceptedFileTypes }) => {
                const messages: Record<DropzoneRejectionCode, string> = {
                  'file-too-large': t('grants.grantDetails.candidateTasks.form.dropzone.errors.fileTooLarge', {
                    fileName,
                    maxSize: maxSizeFormatted
                  }),
                  'file-invalid-type': t('grants.grantDetails.candidateTasks.form.dropzone.errors.fileInvalidType', {
                    fileName,
                    acceptedFileTypes
                  }),
                  'too-many-files': t('grants.grantDetails.candidateTasks.form.dropzone.errors.tooManyFiles', {
                    fileName
                  })
                }

                return messages[code]
              }}
            />
          </Flex>
        )
      case 'date':
      case 'scheduled_event':
        return (
          <Flex direction="column" w="100%">
            <DateInput
              name={id}
              value={value ? new Date(value as string) : null}
              clearable
              utc
              onChange={date => handleChange(id, date ? date.toISOString() : '')}
              placeholder={placeholder}
              data-testid={`q-${id}-date-input`}
              status={error && touched ? 'danger' : undefined}
              disabled={disabled}
            />
          </Flex>
        )
      default:
        return (
          <Flex direction="column" w="100%">
            <TextInput
              name={id}
              type={type === 'website' ? 'url' : type}
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={e => {
                handleChange(id, e.target.value)
              }}
              onBlur={() => handleBlur?.(id)}
              data-testid={`q-${id}-${type}-input`}
              status={error && touched ? 'danger' : undefined}
              disabled={disabled}
            />
          </Flex>
        )
    }
  }

  return (
    <Flex direction="column" gap={16}>
      <FormField
        label={label}
        necessity={required ? 'required' : 'optional'}
        optionalText={t('grants.grantDetails.candidateTasks.form.optional')}
        status={error && touched ? 'danger' : undefined}
        statusMessage={error && touched ? error : undefined}
        description={renderDescription()}
        instructions={instructions}
      >
        <Flex flex={1} w="100%">
          {renderQuestion()}
        </Flex>
      </FormField>
    </Flex>
  )
}
